import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ListCriteriaService } from '../services/list-criteria.service';
import { ColumnFilters } from '../models/ui/table-column.model';

const RESERVED_QUERY_PARAMS = new Set(['page', 'size', 'asset_type']);

@Component({
    template: ''
})
export abstract class BaseListComponent implements OnInit, OnDestroy {
    protected router = inject(Router);
    protected route = inject(ActivatedRoute);
    protected confirmationService = inject(ConfirmationService);
    protected listCriteria = inject(ListCriteriaService);

    // Dialog & UI State
    itemDialog: boolean = false;
    submitted: boolean = false;

    // Pagination & Filter State
    first: number = 0;
    rows: number = 25;
    columnFilters: ColumnFilters = {};

    /** Convenience for lists that still use a single keywords search box. */
    get keywords(): string {
        return this.columnFilters['keywords'] || '';
    }

    set keywords(value: string) {
        const trimmed = (value || '').trim();
        if (trimmed) {
            this.columnFilters = { ...this.columnFilters, keywords: value };
        } else {
            const { keywords: _removed, ...rest } = this.columnFilters;
            this.columnFilters = rest;
        }
    }

    // Observables
    protected searchSubject = new Subject<ColumnFilters>();
    protected destroy$ = new Subject<void>();
    protected hasLoadedFromUrl = false;

    ngOnInit(): void {
        // Read URL params and restore state
        this.route.queryParams.pipe(
            takeUntil(this.destroy$)
        ).subscribe(params => {
            const page = params['page'] ? parseInt(params['page'], 10) : 1;
            const size = params['size'] ? parseInt(params['size'], 10) : 25;
            const columnFilters = this.parseColumnFilters(params);

            this.rows = size;
            this.first = (page - 1) * size;
            this.columnFilters = columnFilters;
            this.persistListCriteria(page, size, columnFilters);

            // Load data with URL params (only on first load)
            if (!this.hasLoadedFromUrl) {
                this.hasLoadedFromUrl = true;
                this.loadData(page, size, columnFilters);
            }
        });

        // Set up debounced search
        this.searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
            takeUntil(this.destroy$)
        ).subscribe(filters => {
            this.performSearch(filters);
        });
    }

    ngOnDestroy(): void {
        // Ensure latest criteria survive leave → form → list round-trips
        const page = Math.floor(this.first / this.rows) + 1;
        this.persistListCriteria(page, this.rows, this.columnFilters);
        this.searchSubject.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }

    // Abstract method to be implemented by child components
    abstract loadData(page: number, size: number, filters: ColumnFilters): void;

    // Common methods
    onPage(event: any): void {
        this.first = event.first;
        this.rows = event.rows;
        const page = Math.floor(this.first / this.rows) + 1;
        this.updateUrlParams(page, this.rows, this.columnFilters);
        this.loadData(page, this.rows, this.columnFilters);
    }

    onFilter(value: string | ColumnFilters): void {
        const filters: ColumnFilters = typeof value === 'string'
            ? (value.trim() ? { keywords: value } : {})
            : value;
        this.columnFilters = filters;
        this.searchSubject.next(filters);
    }

    get size(): number {
        return this.rows;
    }
    
    set size(value: number) {
        this.rows = value;
    }

    performSearch(filters: ColumnFilters): void {
        this.first = 0;
        this.columnFilters = filters;
        const page = 1;
        this.updateUrlParams(page, this.rows, this.columnFilters);
        this.loadData(page, this.rows, this.columnFilters);
    }

    clearFilter(): void {
        this.columnFilters = {};
        this.first = 0;
        const page = 1;
        this.updateUrlParams(page, this.rows, this.columnFilters);
        this.loadData(page, this.rows, this.columnFilters);
    }

    /** Reload current page with existing pagination and filters intact. */
    reloadCurrentPage(): void {
        const page = Math.floor(this.first / this.rows) + 1;
        this.loadData(page, this.rows, this.columnFilters);
    }

    protected parseColumnFilters(params: Params): ColumnFilters {
        const filters: ColumnFilters = {};
        for (const [key, value] of Object.entries(params)) {
            if (RESERVED_QUERY_PARAMS.has(key)) {
                continue;
            }
            if (typeof value === 'string' && value.trim()) {
                filters[key] = value;
            }
        }
        return filters;
    }

    protected updateUrlParams(page: number, size: number, filters: ColumnFilters): void {
        const queryParams: Record<string, string | number> = {
            page: page,
            size: size,
            ...filters
        };

        this.persistListCriteria(page, size, filters);

        // Use replaceUrl to avoid adding to browser history on every change
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: queryParams,
            replaceUrl: true
        });
    }

    /** Current list path without query string (e.g. /bridges). */
    protected getListPath(): string {
        return this.router.url.split('?')[0] || '/';
    }

    protected persistListCriteria(page: number, size: number, filters: ColumnFilters): void {
        this.listCriteria.save(this.getListPath(), { page, size, columnFilters: filters });
    }

    hideDialog() {
        this.itemDialog = false;
        this.submitted = false;
    }
}
